import type { PairingCodeDto, PairingStatusDto } from '@pl/domain';
import { useEffect, useRef, useState } from 'react';
import { Icon } from '../ui/Icon';
import { useNow } from './useWallData';

export const POLL_MS = 3000;
/** The longest wait between polls after the server has asked us to slow down. */
const MAX_BACKOFF_MS = 60_000;

/** "482913" → "482 913": two groups a person can read off a TV across a room. */
export const spacedCode = (code: string) => `${code.slice(0, 3)} ${code.slice(3)}`;

/**
 * How far the server's clock is ahead of this TV's, read off the answer's `Date` header; 0 when it
 * has none. TV clocks are often wrong, and a code's expiry is on the server's clock.
 */
function serverAheadMs(res: Response): number {
  const at = Date.parse(res.headers.get('date') ?? '');
  return Number.isFinite(at) ? at - Date.now() : 0;
}

/** Seconds the server asked us to wait (`Retry-After`), or null. */
function retryAfterMs(res: Response): number | null {
  const v = Number(res.headers.get('retry-after'));
  return Number.isFinite(v) && v > 0 ? v * 1000 : null;
}

/**
 * The screen a TV shows until an admin pairs it: a code to type into Office → Settings → Devices,
 * and a poll for the answer. The poll secret lives only in this component's memory — never in a URL,
 * never stored — so a reload simply asks for a new code. Once paired, the server sets the device
 * cookie on the poll's answer, and `onPaired` sends the TV to its board.
 */
export function Pairing({ onPaired }: { onPaired: () => void }) {
  const [pairing, setPairing] = useState<(PairingCodeDto & { serverAhead: number }) | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  /** The server said the code on screen is gone, and the next one has not arrived yet. */
  const [renewing, setRenewing] = useState(false);
  const now = useNow(1000);
  const onPairedRef = useRef(onPaired);
  onPairedRef.current = onPaired;

  useEffect(() => {
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const later = (fn: () => void, ms: number) => {
      if (!stopped) timer = setTimeout(fn, ms);
    };

    async function newCode(): Promise<void> {
      let res: Response;
      try {
        res = await fetch('/api/v1/devices/pairing-codes', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'content-type': 'application/json', 'x-requested-with': 'pallet-level' },
          body: '{}',
        });
      } catch {
        if (stopped) return;
        setProblem('Cannot reach the server. Trying again…');
        return later(newCode, 5000);
      }
      if (stopped) return;
      if (res.ok) {
        const code = (await res.json()) as PairingCodeDto;
        if (stopped) return;
        setProblem(null);
        setRenewing(false);
        setPairing({ ...code, serverAhead: serverAheadMs(res) });
        return later(() => void poll(code, POLL_MS), POLL_MS);
      }
      // 429: too many codes from this address; 503: no free code. Both clear up with time.
      setProblem(null);
      later(newCode, retryAfterMs(res) ?? MAX_BACKOFF_MS);
    }

    // When a code has expired is the server's to say, with a 404: this TV's clock may be minutes out.
    async function poll(code: PairingCodeDto, every: number): Promise<void> {
      let res: Response;
      try {
        res = await fetch(`/api/v1/devices/pairing-codes/${encodeURIComponent(code.code)}`, {
          credentials: 'same-origin',
          cache: 'no-store',
          headers: { 'x-pairing-secret': code.pollSecret },
        });
      } catch {
        return later(() => void poll(code, every), every);
      }
      if (stopped) return;
      if (res.status === 429) {
        // Asked to slow down: wait as long as we are told, or twice as long as last time.
        const wait = retryAfterMs(res) ?? Math.min(every * 2, MAX_BACKOFF_MS);
        return later(() => void poll(code, wait), wait);
      }
      if (res.status === 404) {
        // Expired or unknown: take the dead code off the screen, and start again.
        setPairing(null);
        setRenewing(true);
        return void newCode();
      }
      if (!res.ok) return later(() => void poll(code, every), every);
      const status = (await res.json()) as PairingStatusDto;
      if (stopped) return;
      if (status.status === 'paired') return onPairedRef.current();
      later(() => void poll(code, POLL_MS), POLL_MS);
    }

    void newCode();
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
    };
  }, []);

  // Counted down on the server's clock, so a TV whose clock is out still shows the time left.
  const left = pairing ? Math.max(0, Date.parse(pairing.expiresAt) - (now + pairing.serverAhead)) : 0;
  const countdown = `${Math.floor(left / 60_000)}:${String(Math.floor((left % 60_000) / 1000)).padStart(2, '0')}`;

  return (
    <div className="wall">
      <div className="rail">
        <span>
          <b>TV</b> · NOT PAIRED YET
        </span>
      </div>
      <div className="w-stage pair-stage">
        <div className="brand-row">
          <div className="w-mark">
            <i />
          </div>
          <div className="wordmark">
            PALLET<span>·</span>LEVEL
          </div>
        </div>
        {pairing ? (
          <section className="pair" aria-label="Pairing">
            <div className="w-k">Pairing code</div>
            <div className="code" data-testid="pairing-code">
              {spacedCode(pairing.code)}
            </div>
            <div className="how">
              On a computer:
              <br />
              <span className="path">
                Office <em>→</em> Settings <em>→</em> Devices <em>→</em> Pair a TV
              </span>
              ,<br />
              then enter this code
            </div>
            <div className="status">
              <div>
                <Icon name="clock" />
                <span className="muted2">Code expires in</span>
                <span className="mono">{countdown}</span>
              </div>
              <div>
                <span className="pulse" />
                <span>Waiting for pairing…</span>
              </div>
            </div>
          </section>
        ) : (
          <div className="boot">{problem ?? (renewing ? 'Getting a new code…' : 'Getting a code…')}</div>
        )}
        <div className="foot">
          <Icon name="tv" />
          Works in this TV's browser
        </div>
      </div>
    </div>
  );
}
